import utils from '@/helpers/utils'
import { pick, findIndex } from 'lodash'
import store from '@/store'

export default function(_this) {
  return {
    /**
     * Set options for DataGrid
     * @param {String} gridId - DataGrid id
     * @param {Object} options - options
     */
    setOptions(gridId, options) {
      return _this
        .getRef('widgetsData/dataGrid')
        .child(gridId)
        .set(options)
    },
    /**
     * Get options of data grid
     * @param {string} gridId - grid id
     * @param {function} callback - callback function
     */
    read(gridId, callback) {
      _this.getRef(`widgetsData/dataGrid/${gridId}`).on('value', snapshot => {
        callback(snapshot.val())
      })
    },
    readAll(callback) {
      _this.getRef('widgetsData/dataGrid').on('value', snapshot => {
        callback(snapshot.val())
      })
    },
    /**
     * Change grid title
     * @param {String} data.gridId - grid id
     * @param {String} data.value - new title of grid
     * @param {String} - viewe key
     */
    editTitle(data) {
      const { gridId, value, view } = data
      const ref = view
        ? `widgetsData/dataGrid/${gridId}/views/${view}/title`
        : `widgetsData/dataGrid/${gridId}/title`

      _this.getRef(ref).set(value, err => {
        if (err) console.error(err)
      })
    },
    /**
     * Change order of columns in DataGrid component
     * @param {String} data.gridId - grid id
     * @param {String} data.view - view key
     * @param {Object} data.newMeta - object collection of a new meta data
     */
    setNewColumnOrder(data) {
      const { gridId, view } = data
      let { newMeta } = data
      const ref = view
        ? `widgetsData/dataGrid/${gridId}/views/${view}/meta`
        : `widgetsData/dataGrid/${gridId}/meta`

      if (view) {
        const metaReady = {}

        for (let cellKey in newMeta) {
          metaReady[cellKey] = pick(newMeta[cellKey], ['order', 'invisible'])
        }

        newMeta = metaReady
      }

      _this.getRef(ref).set(newMeta, err => {
        if (err) console.error(err)
      })
    },
    /**
     * Save new row editor form data
     * @param {string} gridId - GridData component id
     * @param {Object} form - row editor form data
     */
    saveRowEditorForm(gridId, form, cb) {
      _this
        .getRef(`widgetsData/dataGrid/${gridId}/rowEditorFieldsForm`)
        .set(JSON.stringify(form), () => {
          if (cb) cb()
        })
    },
    /**
     * Save option to db
     * @param {string} data.gridId - DataGrid component id
     * @param {string} data.oName - option name
     * @param {string} data.oValue - option value
     * @param {function} cb - callback function
     */
    saveOption(data, cb) {
      const { gridId, oName, oValue } = data

      _this
        .getRef('widgetsData/dataGrid')
        .child(gridId)
        .child(oName)
        .set(oValue, () => {
          if (cb) cb()
        })
    },
    /**
     * Define column visibility value
     * @param {String} data.gridId - gird id
     * @param {String} data.view - view key
     * @param {String} data.cellKey - key of column
     * @param {Boolean} data.value - value, that define column visibility
     */
    async changeColumnVisability(data) {
      const { gridId, view, cellKey, value, form } = data
      const ref1 = view
        ? `widgetsData/dataGrid/${gridId}/views/${view}/meta`
        : `widgetsData/dataGrid/${gridId}/meta`
      const ref2 = view
        ? `widgetsData/dataGrid/${gridId}/views/${view}/rowEditorFieldsForm`
        : `widgetsData/dataGrid/${gridId}/rowEditorFieldsForm`

      _this
        .getRef(ref1)
        .child(cellKey)
        .child('invisible')
        .set(value || null)
/* 
      for (let section of form.sections || []) {
        for (let rows of section.rows || []) {
          const idx = findIndex(rows.controls || [], { propName: cellKey })

          if (idx) {
            const control = rows.controls[idx]

            control.invisible = value

            break
          }
        }
      }

      _this.getRef(ref2).set(JSON.stringify(form))
      */
    },
    /**
     * Save grid filter
     * @param {String} data.gridId - gird id
     * @param {String} data.view - view key
     * @param {Object} data.filterBuilder - filterBuilder state
     */
    saveFilterBuilder(data) {
      const { gridId, view, filterBuilder = {} } = data
      const ref = view
        ? `widgetsData/dataGrid/${gridId}/views/${view}/filterBuilder`
        : `widgetsData/dataGrid/${gridId}/filterBuilder`
      const { rules = [] } = filterBuilder

      if (rules.length > 0) {
        _this.getRef(ref).set(JSON.stringify(filterBuilder))
      } else {
        _this.getRef(ref).remove()
      }
    },
    /**
     * Create view instance for DataGrid and Widget provided
     * @param {String} data.gridId - DataGrid id
     * @param {String} data.widgetKey - widget key
     * @param {String} data.title - view title
     * @param {Boolean} data.inherited - is widget inherited
     */
    async createView({ gridId, widgetKey, title, inherited }) {
      const template = store.state.currentTemplate
      const options = await new Promise(resolve => {
        if (template) {
          _this.root
            .ref('dashboards')
            .child(template)
            .child('widgetsData/dataGrid')
            .child(gridId)
            .once('value', snapshot => {
              resolve(snapshot.val())
            })
        } else {
          _this.dataGrid.options.read(gridId, o => {
            resolve(o)
          })
        }
      })

      let widget = await _this.widgets.readOne(widgetKey)

      if (!widget && template) {
        widget = await new Promise(resolve => {
          _this.root
            .ref('dashboards')
            .child(template)
            .child('widgets')
            .child(widgetKey)
            .once('value', snapshot => {
              resolve(snapshot.val())
            })
        })
      }

      const props = widget.props || {}
      let newView = {}
      const viewKey = utils.sanitize(title)

      if (props.view && options.views && options.views[props.view]) {
        const currentView = options.views[props.view]
        newView = Object.assign(currentView, { title })
      } else {
        const meta = {}

        for (let cellKey in options.meta) {
          meta[cellKey] = pick(options.meta[cellKey], ['order', 'invisible'])
        }

        newView = { title, meta }
        if (options.filterBuilder) newView.filterBuilder = options.filterBuilder
      }

      await new Promise(resolve => {
        _this
          .getRef('widgetsData/dataGrid')
          .child(gridId)
          .child('views')
          .child(viewKey)
          .set(newView, () => resolve())
      })

      this.setView(widgetKey, viewKey, inherited)
    },
    /**
     * Set view for DataGrid widget
     * @param {String} widgetKey - widget key
     * @param {String} viewKey - view key
     * @param {Boolean} inherited - is inherited
     */
    setView(widgetKey, viewKey, inherited) {
      _this
        .getRef('widgets')
        .child(widgetKey)
        .child('props/view')
        .set(viewKey)

      if (inherited) {
        _this
          .getRef('widgets')
          .child(widgetKey)
          .child('props/extended')
          .child(viewKey)
          .set(true)
      }
    },
    /**
     * Remove view from DataGrid
     * @param {String} data.gridId - DataGrid id
     * @param {String} data.viewKey - view key
     */
    async removeView({ gridId, viewKey }) {
      _this
        .getRef('widgetsData/dataGrid')
        .child(gridId)
        .child('views')
        .child(viewKey)
        .remove()
    },
  }
}
